import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Banner } from "../../../_components/Contacts/ContactShared";
import { describeBulkOutcome } from "../../../_components/Shared/BulkApproval";
import PurchaseDocList from "../../../_components/Purchasing/PurchaseDocList";
import {
    getPurchaseApprovalList,
    isFinalApprovalDecision,
} from "../../../_services/approval.service";
import {
    FiClock,
    FiUser,
    FiDollarSign,
    FiHome,
    FiFileText
} from "react-icons/fi";
import { T, fmtCurrency, ModuleApprovalActions } from "../../../_components/Sales/SalesShared";
import { DocStatusBadge as PurchaseStatusBadge } from "../../../_components/Purchasing/PurchaseShared";

const APPROVAL_STATUSES = ["All", "Pending for Approval", "Approved", "Rejected"];

const PURCHASE_SEARCH_OPTIONS = [
    { key: "all", label: "All" },
    { key: "id", label: "PO No." },
    { key: "date", label: "Date" },
    { key: "supplier", label: "Company Name" },
    { key: "contactPerson", label: "Contact Person" }
];

export default function PurchasingApprovals() {
    const [searchParams, setSearchParams] = useSearchParams();
    const [refreshKey, setRefreshKey] = useState(0);
    const [banner, setBanner] = useState({ type: "", msg: "" });
    const bannerTimerRef = useRef(null);

    const showBanner = useCallback((type, msg, duration = 4000) => {
        clearTimeout(bannerTimerRef.current);
        setBanner({ type, msg });
        bannerTimerRef.current = setTimeout(
            () => setBanner({ type: "", msg: "" }),
            duration
        );
    }, []);

    useEffect(() => () => clearTimeout(bannerTimerRef.current), []);

    // Default to "Pending for Approval" when first entering the page
    useEffect(() => {
        if (!searchParams.get("status")) {
            setSearchParams(
                {
                    status: "Pending for Approval",
                    page: "1"
                },
                { replace: true }
            );
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const getPurchaseApprovals = useCallback(
        async (qp, options = {}) => {
            void refreshKey;
            const result = await getPurchaseApprovalList(qp, options);
            return {
                ...result,
                data: (result.data || []).map((x) => ({
                    ...x,
                    _docType: "Purchase Order",
                    _basePath: "/purchases/purchase-orders",
                    _typeKey: x.typeKey,
                })),
            };
        },
        [refreshKey]
    );

    const handleDecision = useCallback((item, onClose, result, { action }) => {
        onClose();
        setRefreshKey((current) => current + 1);
        const finalApproval = action === "approve" && isFinalApprovalDecision(result);
        showBanner(
            "success",
            action === "reject"
                ? `Document ${item.id} rejected successfully.`
                : finalApproval
                    ? `Document ${item.id} approved successfully.`
                    : `Approval for document ${item.id} recorded. Pending the next approval level.`
        );
    }, [showBanner]);

    const handleBulkComplete = useCallback((outcome) => {
        setRefreshKey((current) => current + 1);
        const { type, msg } = describeBulkOutcome(outcome);
        showBanner(type, msg, outcome.failed.length ? 15000 : 4000);
    }, [showBanner]);

    const renderPurchasePreviewActions = useCallback(
        (item, onClose, onView) => {
            return (
                <div style={{ display: "flex", flexDirection: "column", gap: 10, width: "100%" }}>
                    {item.status === "Pending for Approval" && (item.canApprove || item.canReject) && (
                        <ModuleApprovalActions
                            block
                            docType={item._docType}
                            key={`${item.recordType}:${item.dbId}`}
                            recordType={item.recordType}
                            recordId={item.dbId}
                            recordLabel={item.id}
                            currentLevel={item.currentLevel}
                            canApprove={item.canApprove}
                            canReject={item.canReject}
                            onDecision={(result, decision) => handleDecision(item, onClose, result, decision)}
                            onError={(error) => showBanner("danger", error.message || `Failed to process document ${item.id}.`)}
                        />
                    )}

                    <button
                        className="cl-preview-btn-primary"
                        onClick={() => onView(`${item._basePath}/${item.id}?fromApprovals=true`)}
                        style={{
                            width: "100%",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            gap: 8,
                            padding: "12px 16px",
                            fontSize: 14
                        }}
                    >
                        <FiFileText size={16} /> View Document Details
                    </button>
                </div>
            );
        },
        [handleDecision, showBanner]
    );

    const purchaseColumns = useMemo(
        () => [
            {
                label: "Doc. No.",
                key: "id",
                width: 110,
                render: (v) => (
                    <span style={{ color: T.textLight, fontWeight: 700, fontSize: 13 }}>{v}</span>
                )
            },
            {
                label: "Type",
                key: "_docType",
                width: 140,
                render: (v) => (
                    <span style={{ fontWeight: 600, color: T.accent, fontSize: 13 }}>{v}</span>
                )
            },
            {
                label: "Date",
                key: "date",
                width: 110,
                render: (v) => <span style={{ color: T.textMuted, fontSize: 13 }}>{v}</span>
            },
            {
                label: "Company Name",
                key: "supplier",
                width: 220,
                render: (v) => (
                    <span
                        style={{
                            fontWeight: 600,
                            display: "inline-block",
                            maxWidth: 210,
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis"
                        }}
                    >
                        {v}
                    </span>
                )
            },
            {
                label: "Contact Person",
                key: "contactPerson",
                render: (v) => <span style={{ color: T.textMuted, fontSize: 13 }}>{v || "—"}</span>
            },
            { label: "Status", key: "status", render: (v) => <PurchaseStatusBadge value={v} /> }
        ],
        []
    );

    const purchasePreviewFields = useMemo(
        () => [
            { icon: FiFileText, label: "Document Type", key: "_docType" },
            { icon: FiClock, label: "Date", key: "date" },
            { icon: FiHome, label: "Company Name", key: "supplier" },
            { icon: FiUser, label: "Contact Person", key: "contactPerson" },
            { icon: FiDollarSign, label: "Total Amount", key: "grandTotal", render: (v, row) => fmtCurrency(v ?? row.payAmount, row.currency) }
        ],
        []
    );

    return (
        <div style={{ padding: "0 20px" }}>
            <Banner type={banner.type} msg={banner.msg} onClose={() => setBanner({ type: "", msg: "" })} />

            <div style={{ marginBottom: 20 }}>
                <h4 style={{ fontWeight: 700, color: T.textPrimary, margin: 0, fontSize: 24 }}>
                    Purchase Approvals
                </h4>
                <p style={{ fontSize: 14, color: T.textMuted, margin: "4px 0 0" }}>
                    Review and process purchase documents submitted for approval.
                </p>
            </div>

            <PurchaseDocList
                onBulkComplete={handleBulkComplete}
                docType="Purchase Document"
                basePath="/purchases"
                fetchList={getPurchaseApprovals}
                statuses={APPROVAL_STATUSES}
                customColumns={purchaseColumns}
                renderPreviewActions={renderPurchasePreviewActions}
                hideCreate={true}
                hideExport={true}
                searchOptions={PURCHASE_SEARCH_OPTIONS}
                previewFields={purchasePreviewFields}
            />
        </div>
    );
}
